import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Progress } from '@/components/ui/progress';
import { Target, Sparkles, ArrowRight, Loader2, CheckCircle, AlertCircle, MessageSquare } from 'lucide-react';
import api, { LOCATIONS, PROJECT_TYPES, FINISH_LEVELS, formatZARFull, trackEvent } from '@/lib/api';
import ReportLeadGate from '@/components/ReportLeadGate';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';

const SCORE_TIERS = {
  high_priority: { label: 'Strong Fit', color: 'text-green-600', bg: 'bg-green-50', border: 'border-green-200', desc: 'Your project shows strong alignment. You are well-positioned to move forward.' },
  medium_priority: { label: 'Developing', color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-200', desc: 'Your project has potential but some areas need attention before proceeding.' },
  nurture: { label: 'Early Stage', color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-200', desc: 'Your project is in early stages. The guidance below will help you build a stronger foundation.' },
};

export default function ProjectFitScore() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    budget: '', project_type: 'new_build', location: '', finish_level: 'standard',
    size_sqm: '', has_brief: false, has_timeline: false, has_site: false,
    email: '', name: '', phone: '',
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [aiExplanation, setAiExplanation] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [showGate, setShowGate] = useState(false);
  const [gateSubmitted, setGateSubmitted] = useState(false);

  useEffect(() => { trackEvent('page_view', { page: 'fit_score' }); }, []);

  const handleCalculate = async () => {
    if (!form.budget || !form.location) { toast.error('Please enter your budget and location'); return; }
    api.post('/tools/impression', {
      tool: 'fit_score',
      criteria: {
        budget: Number(form.budget), project_type: form.project_type, location: form.location,
        finish_level: form.finish_level, size_sqm: form.size_sqm ? Number(form.size_sqm) : null,
        has_brief: form.has_brief, has_timeline: form.has_timeline, has_site: form.has_site,
      },
    }).catch(() => {});
    if (gateSubmitted) { runCalculation(); return; }
    setShowGate(true);
  };

  const runCalculation = async (contact) => {
    setLoading(true); setResult(null); setAiExplanation('');
    try {
      if (contact) setForm((f) => ({ ...f, ...contact }));
      // First get cost estimate for budget realism
      let estimatedCost = Number(form.budget);
      if (form.size_sqm) {
        const { data: costData } = await api.post('/cost/calculate', {
          project_type: form.project_type, size_sqm: Number(form.size_sqm),
          finish_level: form.finish_level, location: form.location,
        });
        estimatedCost = (costData.total_cost.min + costData.total_cost.max) / 2;
      }
      const { data } = await api.post('/fit-score/calculate', {
        budget: Number(form.budget), estimated_cost: estimatedCost,
        complexity: form.size_sqm ? Math.min(Number(form.size_sqm) / 100, 10) : 5,
        has_brief: form.has_brief, has_timeline: form.has_timeline,
        has_site: form.has_site, finish_level: form.finish_level,
        project_type: form.project_type,
        email: (contact?.email) || form.email || undefined,
        name: (contact?.name) || form.name || undefined,
        phone: (contact?.phone) || form.phone || undefined,
      });
      setResult(data);
      setGateSubmitted(true);
      setShowGate(false);
      trackEvent('fit_score_calculated', { score: data.total_score, recommendation: data.recommendation });
      toast.success('Fit score calculated');
      fetchAiExplanation(data);
    } catch (e) {
      toast.error('Calculation failed');
      throw e;
    } finally { setLoading(false); }
  };

  const handleGateSubmit = async ({ name, email, phone }) => {
    setForm((f) => ({ ...f, name, email, phone }));
    await runCalculation({ name, email, phone });
    trackEvent('fit_score_lead_captured', { email });
  };

  const fetchAiExplanation = async (data) => {
    setAiLoading(true);
    try {
      const { data: aiData } = await api.post('/ai/explain', { type: 'fit_score', data });
      setAiExplanation(aiData.explanation);
    } catch { /* AI is optional */ } finally { setAiLoading(false); }
  };

  const tier = result ? SCORE_TIERS[result.recommendation] || SCORE_TIERS.nurture : null;

  return (
    <div className="min-h-screen bg-brand-light pt-20" data-testid="fit-score-page">
      <div className="section-dark py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <p className="text-brand-accent font-heading text-xs font-bold uppercase tracking-[0.2em] mb-3">Advisory Tool</p>
          <h1 className="font-heading text-4xl md:text-5xl tracking-tighter font-medium text-white mb-4">Are You Ready to Start?</h1>
          <p className="text-white/50 font-body text-base max-w-2xl">
            An honest readiness check. This evaluates budget realism, scope clarity, and preparation — so you avoid wasted time and misalignment.
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-sm border border-black/10 p-8 mb-8" data-testid="fit-score-form">
          <h2 className="font-heading text-lg font-medium mb-6">Your Project Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold text-brand-dark/60 mb-1.5">Project Type</Label>
              <Select value={form.project_type} onValueChange={(v) => setForm(p => ({ ...p, project_type: v }))}>
                <SelectTrigger data-testid="fit-select-type" className="rounded-sm h-11"><SelectValue /></SelectTrigger>
                <SelectContent>{PROJECT_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold text-brand-dark/60 mb-1.5">Location *</Label>
              <Select value={form.location} onValueChange={(v) => setForm(p => ({ ...p, location: v }))}>
                <SelectTrigger data-testid="fit-select-location" className="rounded-sm h-11"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{LOCATIONS.map(l => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold text-brand-dark/60 mb-1.5">Budget (R) *</Label>
              <Input data-testid="fit-input-budget" type="number" value={form.budget} onChange={(e) => setForm(p => ({ ...p, budget: e.target.value }))} placeholder="e.g. 5000000" className="rounded-sm h-11" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold text-brand-dark/60 mb-1.5">Desired Size (m²)</Label>
              <Input data-testid="fit-input-size" type="number" value={form.size_sqm} onChange={(e) => setForm(p => ({ ...p, size_sqm: e.target.value }))} placeholder="e.g. 250 (optional)" className="rounded-sm h-11" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold text-brand-dark/60 mb-1.5">Finish Level</Label>
              <Select value={form.finish_level} onValueChange={(v) => setForm(p => ({ ...p, finish_level: v }))}>
                <SelectTrigger data-testid="fit-select-finish" className="rounded-sm h-11"><SelectValue /></SelectTrigger>
                <SelectContent>{FINISH_LEVELS.map(f => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          <div className="mt-6 space-y-3">
            <Label className="text-xs uppercase tracking-wider font-semibold text-brand-dark/60 block mb-2">Readiness Indicators</Label>
            <label className="flex items-center gap-3 cursor-pointer" data-testid="fit-has-site">
              <Checkbox checked={form.has_site} onCheckedChange={(c) => setForm(p => ({ ...p, has_site: c }))} />
              <span className="text-sm font-body">I have a specific site or stand identified</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer" data-testid="fit-has-timeline">
              <Checkbox checked={form.has_timeline} onCheckedChange={(c) => setForm(p => ({ ...p, has_timeline: c }))} />
              <span className="text-sm font-body">I have a timeline in mind for this project</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer" data-testid="fit-has-brief">
              <Checkbox checked={form.has_brief} onCheckedChange={(c) => setForm(p => ({ ...p, has_brief: c }))} />
              <span className="text-sm font-body">I have a project brief or clear requirements</span>
            </label>
          </div>

          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold text-brand-dark/60 mb-1.5">Email (optional)</Label>
              <Input data-testid="fit-input-email" type="email" value={form.email} onChange={(e) => setForm(p => ({ ...p, email: e.target.value }))} placeholder="you@email.com" className="rounded-sm h-10" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold text-brand-dark/60 mb-1.5">Name (optional)</Label>
              <Input data-testid="fit-input-name" value={form.name} onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Your name" className="rounded-sm h-10" />
            </div>
          </div>

          <Button data-testid="fit-calculate-btn" onClick={handleCalculate} disabled={loading} className="mt-8 bg-brand-dark text-white hover:bg-gray-800 rounded-sm h-12 px-10 font-body text-sm font-semibold uppercase tracking-wider">
            {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Assessing...</> : <>Check Your Readiness <Target className="w-4 h-4 ml-2" /></>}
          </Button>
        </div>

        <AnimatePresence>
          {result && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6" data-testid="fit-score-results">
              {/* Score Display */}
              <div className={`rounded-sm border p-8 ${tier.bg} ${tier.border}`}>
                <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                  <div>
                    <p className="text-xs font-heading uppercase tracking-wider text-brand-dark/40 mb-2">Your Project Fit Score</p>
                    <div className="flex items-baseline gap-3">
                      <span className={`font-heading text-5xl font-medium ${tier.color}`} data-testid="fit-score-value">{result.total_score}</span>
                      <span className="text-brand-dark/40 font-heading text-xl">/100</span>
                    </div>
                    <p className={`font-heading text-sm font-bold uppercase tracking-wider mt-2 ${tier.color}`}>{tier.label}</p>
                    <p className="text-brand-dark/60 font-body text-sm mt-2 max-w-lg">{tier.desc}</p>
                  </div>
                  <div className="w-full md:w-48">
                    <div className="space-y-3">
                      {[
                        { label: 'Budget Realism', value: result.budget_realism },
                        { label: 'Scope Clarity', value: result.scope_complexity },
                        { label: 'Readiness', value: result.readiness },
                      ].map((m) => (
                        <div key={m.label}>
                          <div className="flex justify-between text-xs font-body mb-1">
                            <span className="text-brand-dark/50">{m.label}</span>
                            <span className="font-semibold">{m.value}</span>
                          </div>
                          <Progress value={m.value} className="h-1.5" data-testid={`fit-progress-${m.label.toLowerCase().replace(/\s/g, '-')}`} />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Improvements */}
              {result.improvements?.length > 0 && (
                <div className="bg-white rounded-sm border border-black/10 p-6" data-testid="fit-improvements">
                  <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-brand-dark/60 mb-4">What Improves Your Score</h3>
                  <div className="space-y-3">
                    {result.improvements.map((imp, i) => (
                      <div key={i} className="flex gap-3 items-start">
                        <AlertCircle className="w-4 h-4 text-brand-accent mt-0.5 flex-shrink-0" />
                        <p className="text-sm font-body text-brand-dark/70">{imp}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* AI Explanation */}
              <div className="bg-white rounded-sm border border-black/10 p-6" data-testid="fit-ai-explanation">
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles className="w-4 h-4 text-brand-accent" />
                  <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-brand-dark/60">Advisory Insight</h3>
                </div>
                {aiLoading ? (
                  <div className="flex items-center gap-2 text-brand-dark/40 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Generating advisory...</div>
                ) : aiExplanation ? (
                  <div className="prose prose-sm max-w-none text-brand-dark/70 font-body leading-relaxed whitespace-pre-wrap">{aiExplanation}</div>
                ) : (
                  <p className="text-brand-dark/40 text-sm font-body">Advisory insight will appear after calculation.</p>
                )}
              </div>

              {/* Next Steps */}
              <div className="bg-brand-dark rounded-sm p-8">
                <h3 className="font-heading text-xl font-medium text-white mb-3">Recommended Next Steps</h3>
                <p className="text-white/50 font-body text-sm mb-6">Based on your score, here&apos;s what we suggest:</p>
                <div className="flex flex-wrap gap-4">
                  {!form.has_brief && (
                    <Link to="/brief-builder" data-testid="fit-cta-brief">
                      <Button className="bg-brand-accent text-brand-dark hover:bg-brand-accent/90 rounded-sm font-body text-xs font-semibold uppercase tracking-wider">
                        Build Your Brief <ArrowRight className="w-3.5 h-3.5 ml-2" />
                      </Button>
                    </Link>
                  )}
                  <Link to="/feasibility" data-testid="fit-cta-feasibility">
                    <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 rounded-sm font-body text-xs font-semibold uppercase tracking-wider">
                      Run Feasibility Check <ArrowRight className="w-3.5 h-3.5 ml-2" />
                    </Button>
                  </Link>
                  <Link to="/cost-calculator" data-testid="fit-cta-cost">
                    <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 rounded-sm font-body text-xs font-semibold uppercase tracking-wider">
                      See Cost Reality <ArrowRight className="w-3.5 h-3.5 ml-2" />
                    </Button>
                  </Link>
                  <Button data-testid="fit-ask-btn" variant="outline" onClick={() => navigate('/ask-architect', { state: { context: { type: 'fit_score', data: { total_score: result.total_score, budget_realism: result.budget_realism, scope_complexity: result.scope_complexity, readiness: result.readiness, recommendation: result.recommendation } } } })} className="border-brand-accent/40 text-brand-accent hover:bg-brand-accent/10 rounded-sm font-body text-xs font-semibold uppercase tracking-wider">
                    <MessageSquare className="w-3.5 h-3.5 mr-2" /> What to Think About Next
                  </Button>
                </div>
                <p className="text-brand-accent/60 font-heading italic text-sm mt-6">
                  We create space — for better decisions.
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <ReportLeadGate
        open={showGate}
        onClose={() => setShowGate(false)}
        onSubmit={handleGateSubmit}
        toolLabel="Fit Score"
        initial={{ name: form.name, email: form.email, phone: form.phone }}
      />
    </div>
  );
}
